<script setup>
import { ref, getCurrentInstance, onBeforeUnmount, defineEmits, defineProps, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePageLoadingStore } from '../../../../../../stores/pageLoading'
import mypageJobFairApplicantService from '../../../../services/mypage/jobFair/mypageJobFairApplicantService'
import JobFairResumeDetailModal from '../../../../components/modal/jobFair/JobFairResumeDetailModal.vue'
import JobFairCorpInfoDetailModal from '../../../../components/modal/jobFair/JobFairCorpInfoDetailModal.vue'
import JobFairJobNoticeDetailModal from '../../../../components/modal/jobFair/JobFairJobNoticeDetailModal.vue'

const route = useRoute()
const router = useRouter()
const loadingStore = usePageLoadingStore()
const globals = getCurrentInstance().appContext.config.globalProperties;
const { $pageListBoardNo, alert, confirm } = globals;

const props = defineProps({
  resumeId: {
    type: [String, Number],
    default: null,
  },
  fileYn: {
    type: String,
    default: '',
  },
});

const currentPage = ref(1);
const pageSize = ref(10);
const totalPages = ref(1);
const totalCount = ref(0);
const list = ref([]); 

const jobFairResumeModal = ref(null); // 이력서

const pageSizeGroup = ref([
  { label:"10개씩 보기", value: 10 },
  { label:"30개씩 보기", value: 30 },
  { label:"50개씩 보기", value: 50 },
  { label:"100개씩 보기", value: 100 },
])

const jobNoticeModal = ref(null); // 채용공고
const corpInfoModal = ref(null); // 기업정보

watch(() => props.resumeId, (newResumeId) => {
  if (newResumeId) {
    retrieveList(newResumeId);
  } else {
    list.value = [];
    totalCount.value = 0;
    totalPages.value = 1;
    currentPage.value = 1;
  }
}, { immediate: true });


// 이력서 보기
const onClickMyResume = async () => {
  await jobFairResumeModal.value.show({
    resumeId: props.resumeId,
    showDeleteBtn: true,
   });
}

// 입사지원 목록
const retrieveList = (resumeId) => {
  let paramObj = {
    resumeId,
    pageNumber: currentPage.value || 0,
    size: pageSize.value || 10,
  }

  loadingStore.loading = true;
  mypageJobFairApplicantService.retrieveApplicantListPaging(paramObj)
    .then( (res) => {
      if(res.data.code.toLowerCase() === 'success') {
        const result = res.data.result;
        // console.log(result);
        totalPages.value = result.totalPages;
        totalCount.value = result.totalElements;
        list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
      }
      loadingStore.loading = false;
    })
    .catch( (error) =>  {
      loadingStore.loading = false;
    });
}

// 채용공고 보기
const onClickJobNotice = async (jobNotice) => {
  await jobNoticeModal.value.show({
    jobNoticeSeq: jobNotice.jobNoticeSeq,
    corpId: jobNotice.corpId,
  });
}

// 기업정보 보기
const onClickCorp = async (jobNotice) => {
  const result = await corpInfoModal.value.show({
    corpId: jobNotice.corpId,
    interestYn: jobNotice.interestYn,
  });
}

// 입사지원 상태변경
const onClickApplyStateGb = async (resumeId, jobNoticeSeq, stateGb) => {
  if (stateGb === "01") {
    stateGb = "02"
  }
  const ok = await confirm(["지원을 취소 하시겠습니까?", "'지원취소'로 상태가 변경됩니다."], "지원 취소");
  if(ok) {
    let paramObj = {
      resumeId: resumeId,
      jobNoticeSeq: jobNoticeSeq,
      applyStateGb: stateGb,
    }
    mypageJobFairApplicantService.updateApplyStateGb(paramObj)
			.then((res) => {
				loadingStore.loading = false;
				if(res.data.code.toLowerCase() === 'success') {
					//const result = res.data.result;
					alert(["변경하였습니다."]);
					retrieveList(resumeId);
				} else {
					alert(["변경을 실패하였습니다."]);
				}
			})
			.catch((res) => {
				loadingStore.loading = false;
				alert(["변경을 실패하였습니다."]);
			});
  }
}

const emit = defineEmits(['go-back']);

const goBack = () => {
  emit('go-back');
}
</script>
<template>
    <div class="list-top mb-n-xs">
        <KSASecondaryButton text="이전" @click="goBack()" />
    </div>
    <div class="list-top">
        <p>총 <span>{{ $number(totalCount)}}</span>건</p>
        <KSAPrimaryButton v-show="fileYn === 'N'" text="이력서 보기" @click="onClickMyResume()" />
    </div>
    <div class="table-form">
        <table class="table-form board-mobile-vertical" v-if="list !== null">
        <tr>
            <th>번호</th>
            <th>채용상태</th>
            <th>기업명</th>
            <th>채용공고 제목</th>
            <th>면접 방식</th>
            <th>채용기간</th>
            <th>입사 지원일</th>
            <th>상태</th>
            <th>상태변경</th>
        </tr>
        <tr v-for="applicant in list">
            <td>{{ applicant.boardNo }}</td>
            <td :class="{'gray': applicant.jobNoticeStateGb === '03'}">{{ $gbNameKR('JOB_NOTICE_STATE_GB', applicant.jobNoticeStateGb) }}</td>
            <td class="clickable blue text-overflow long-overflow" @click="onClickCorp(applicant)">{{ applicant.corpName }}</td>
            <td class="clickable blue text-overflow long-overflow" @click="onClickJobNotice(applicant)">{{ applicant.jobName }}</td>
            <td>{{ $gbNameKR('INTV_GB', applicant.intvGb) }}</td>
            <td>{{ $yyyymmdd(applicant.recruitStartYmd) }} ~ {{ $yyyymmdd(applicant.recruitEndYmd) }}</td>
            <td>{{ $yyyymmddHHmmss(applicant.createDate) }}</td>
            <td>
                <span v-if="applicant.applyStateGb === '01'">{{ $gbNameKR('APPLY_STATE_GB',applicant.applyStateGb) }}</span>
                <span v-else-if="applicant.applyStateGb === '03' || applicant.applyStateGb === '05'" class="blue">{{ $gbNameKR('APPLY_STATE_GB',applicant.applyStateGb) }}</span>
                <span v-else-if="applicant.applyStateGb === '04' || applicant.applyStateGb === '06'" class="red">{{ $gbNameKR('APPLY_STATE_GB',applicant.applyStateGb) }}</span>
                <span v-else class="gray">{{ $gbNameKR('APPLY_STATE_GB',applicant.applyStateGb) }}</span>
            </td>
            <td>
							<div v-if="applicant.applyStateGb === '01'">
									<KSATextButton class="clickable black" text="지원취소" @click="onClickApplyStateGb(applicant.resumeId, applicant.jobNoticeSeq, applicant.applyStateGb)"/>
							</div>
							<div v-else>
									-
							</div>
            </td>
        </tr>
        <tr v-if="list.length === 0">
            <td colspan="9">입사 지원 내역이 없습니다.</td>
        </tr>
        </table>
        <Paginate
            v-if="list.length > 0"
            v-model="currentPage"
            :page-count="totalPages"
            :click-handler="onSelectPage"
        />
    </div>
		<JobFairJobNoticeDetailModal ref="jobNoticeModal" /> <!-- 채용공고 보기 -->
  	<JobFairCorpInfoDetailModal ref="corpInfoModal" /> <!-- 기업정보 보기 -->
    <JobFairResumeDetailModal ref="jobFairResumeModal" /> <!-- 특별채용관 이력서 상세 -->
</template>

<style scoped>
.text-overflow.long-overflow {
  max-width: 210px;
}
</style>